import api from './api';

interface SortInfo {
    field: string;
    direction: 'asc' | 'desc';
}

interface FilterParams {
    id?: string;
    requesterId?: string;
    requesterName?: string;
    title?: string;
    description?: string;
    code?: string;
    link?: string;
    meetingLink?: string;
    notes?: string;
    createdAt?: string;
    updatedAt?: string;
    flowType?: string;
    taskType?: string;
    environment?: string;
    startDate?: string;
    endDate?: string;
}

interface PaginatedParams {
    page: number;
    size: number;
    sort: SortInfo[];
    filters?: FilterParams;
}

export const taskService = {
    getAll: async (): Promise<any> => {
        const response = await api.get('/tasks');
        return response.data;
    },

    getAllPaginated: async (params: PaginatedParams): Promise<any> => {
        const {page, size, sort, filters} = params;

        const sortParams = sort.map(s => `${s.field},${s.direction}`);

        const queryParams = new URLSearchParams({
            page: page.toString(),
            size: size.toString(),
        });

        sortParams.forEach(sortParam => {
            queryParams.append('sort', sortParam);
        });

        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach(item => {
                        if (item && item.toString().trim() !== '') {
                            queryParams.append(key, item.toString());
                        }
                    });
                } else if (value && value.toString().trim() !== '') {
                    queryParams.append(key, value.toString());
                }
            });
        }

        const response = await api.get(`/tasks?${queryParams.toString()}`);
        return response.data;
    },

    getUnlinkedPaginated: async (params: PaginatedParams): Promise<any> => {
        const {page, size, sort, filters} = params;

        const sortParams = sort.map(s => `${s.field},${s.direction}`);

        const queryParams = new URLSearchParams({
            page: page.toString(),
            size: size.toString(),
        });

        sortParams.forEach(sortParam => {
            queryParams.append('sort', sortParam);
        });

        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (Array.isArray(value)) {
                    value.forEach(item => {
                        if (item && item.toString().trim() !== '') {
                            queryParams.append(key, item.toString());
                        }
                    });
                } else if (value && value.toString().trim() !== '') {
                    queryParams.append(key, value.toString());
                }
            });
        }

        const response = await api.get(`/tasks/unlinked-billing?${queryParams.toString()}`);
        return response.data;
    },

    getById: async (id: any): Promise<any> => {
        const response = await api.get(`/tasks/${id}`);
        return response.data;
    },

    /**
     * Contadores globais (todas as páginas).
     * Backend: GET /tasks/stats → { total, totalWithoutDelivery, totalWithoutBilling }
     */
    getStats: async (): Promise<{ total: number; totalWithoutDelivery: number; totalWithoutBilling: number }> => {
        const response = await api.get('/tasks/stats');
        return {
            total: response.data?.total ?? 0,
            totalWithoutDelivery: response.data?.totalWithoutDelivery ?? 0,
            totalWithoutBilling:  response.data?.totalWithoutBilling  ?? 0,
        };
    },

    /**
     * Soma de `amount` das tarefas que batem com os filtros (todas as páginas).
     * Backend: GET /tasks/total-amount?<filtros>
     */
    getTotalAmount: async (filters?: Record<string, any>): Promise<number> => {
        const queryParams = new URLSearchParams();
        if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value.toString().trim() !== '') {
                    queryParams.append(key, value.toString());
                }
            });
        }
        const qs = queryParams.toString();
        const response = await api.get(`/tasks/total-amount${qs ? `?${qs}` : ''}`);
        return Number(response.data?.totalAmount ?? 0);
    },

    createWithSubTasks: async (data: any): Promise<any> => {
        const response = await api.post('/tasks/full', data);
        return response.data;
    },

    createWithSubTasksAndFiles: async (data: any, files: File[]): Promise<any> => {
        const formData = new FormData();

        formData.append('task', new Blob([JSON.stringify(data)], {
            type: 'application/json'
        }));

        files.forEach(file => {
            formData.append('files', file);
        });

        const response = await api.post('/tasks/full/with-files', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });

        return response.data;
    },

    updateWithSubTasks: async (id: any, data: any): Promise<any> => {
        const response = await api.put(`/tasks/full/${id}`, data);
        return response.data;
    },

    deleteTaskWithSubTasks: async (id: any): Promise<boolean> => {
        await api.delete(`/tasks/full/${id}`);
        return true;
    },

    deleteBulk: async (ids: number[]): Promise<void> => {
        await api.delete('/tasks/bulk', { data: ids });
    },

    exportToExcel: async (flowType?: string): Promise<Blob> => {
        const params: any = {};
        if (flowType && flowType !== 'TODOS') {
            params.flowType = flowType;
        }
        const response = await api.get('/tasks/export/excel', {
            params,
            responseType: 'blob'
        });
        return response.data;
    },

    exportTasksOnlyToExcel: async (flowType?: string): Promise<Blob> => {
        const params: any = {};
        if (flowType && flowType !== 'TODOS') {
            params.flowType = flowType;
        }
        const response = await api.get('/tasks/export/excel-only', {
            params,
            responseType: 'blob'
        });
        return response.data;
    },

    exportGeneralReport: async (): Promise<Blob> => {
        const response = await api.get('/tasks/export/general-report', {
            responseType: 'blob'
        });
        return response.data;
    },

    exportGeneralReportForUser: async (): Promise<Blob> => {
        const response = await api.get('/tasks/export/general-report-user', {
            responseType: 'blob'
        });
        return response.data;
    },

    sendFinancialEmail: async (taskId: number, additionalEmails?: string[], additionalWhatsAppRecipients?: string[], sendEmail?: boolean, sendWhatsApp?: boolean): Promise<void> => {
        await api.post(`/tasks/${taskId}/send-financial-email`, {
            additionalEmails: additionalEmails || [],
            additionalWhatsAppRecipients: additionalWhatsAppRecipients || [],
            sendEmail: sendEmail !== undefined ? sendEmail : true,
            sendWhatsApp: sendWhatsApp !== undefined ? sendWhatsApp : true
        });
    },

    sendTaskEmail: async (taskId: number, additionalEmails?: string[]): Promise<void> => {
        await api.post(`/tasks/${taskId}/send-task-email`, {
            additionalEmails: additionalEmails || []
        });
    }
};